import { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from './Button';
import { removeWhiteBackground, blobToLogoFile } from '../utils/removeLogoBackground';

// Estilos de logo (espejo de server/src/services/logoGenPresets.ts — solo lo que
// el picker necesita mostrar). El prompt real lo arma el server por `id`.
interface LogoStyleOption {
  id: string;
  label: string;
  description: string;
  icon: string;
}
const LOGO_STYLE_OPTIONS: LogoStyleOption[] = [
  { id: 'wordmark', label: 'Wordmark', description: 'Your brand name in custom lettering', icon: 'Aa' },
  { id: 'monogram', label: 'Monogram', description: 'Your initials in a clean mark', icon: 'AB' },
  { id: 'icon-text', label: 'Icon + name', description: 'A symbol next to your name', icon: '✦' },
  { id: 'emblem', label: 'Emblem', description: 'A badge with your name', icon: '🛡' },
  { id: 'abstract', label: 'Abstract mark', description: 'A symbol only, no text', icon: '◆' },
];

interface Variant {
  taskId: string;
  state: 'waiting' | 'success' | 'fail';
  imageUrl?: string;
}
interface StatusResponse { tasks: Variant[]; }
interface GenerateResponse { taskIds: string[]; style: string; }

interface Props {
  brandName: string;
  colors?: string[];
  industry?: string;
  description?: string;
  // Devuelve el logo elegido YA recortado (PNG transparente) como File + un
  // objectURL para preview, y las URLs de las OTRAS variantes generadas (las que
  // el usuario NO eligió) para guardarlas como brand assets extra. El padre
  // decide cómo persistir (logo elegido isLogo=true; las otras isLogo=false).
  onLogoReady: (file: File, previewUrl: string, extraLogoUrls: string[]) => void;
  onBack?: () => void;
}

// ─── Animations ───────────────────────────────────────────────────────────────
const spin = keyframes`to { transform: rotate(360deg); }`;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; }`;
// Reveal de cada logo cuando llega (pop suave).
const reveal = keyframes`from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: none; }`;
const labelPulse = keyframes`0%,100% { opacity: 0.6; } 50% { opacity: 1; }`;

// Loader de video (mismo asset pre-renderizado que usa la generación de posts):
// llena cada tile mientras el logo se diseña, en vez de un spinner chiquito.
const LoaderVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const LoaderScrim = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 42%, rgba(10,11,22,0.1), rgba(10,11,22,0.72));
`;
const LoaderLabel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #E0F2FE;
  text-shadow: 0 1px 3px rgba(0,0,0,0.65);
  animation: ${labelPulse} 1.6s ease-in-out infinite;
`;

// ─── Styled ─────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const StyleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const StyleCard = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  padding: 10px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.border)};
  background: ${({ $selected }) => ($selected ? 'rgba(59,130,246,0.06)' : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const StyleIcon = styled.span`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const StyleLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;
const StyleDesc = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
`;

// auto-fit + minmax → los tiles ocupan el ancho disponible y nunca quedan
// minúsculos (mín 150px). En pantallas anchas salen los 3 grandes en fila; en
// angostas hacen wrap pero cada logo se ve bien.
const VariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
`;

const VariantTile = styled.button<{ $clickable: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid ${({ $clickable, theme }) => ($clickable ? theme.colors.primary + '55' : theme.colors.border)};
  background: #ffffff;
  padding: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  &:hover {
    border-color: ${({ $clickable, theme }) => ($clickable ? theme.colors.primary : theme.colors.border)};
    transform: ${({ $clickable }) => ($clickable ? 'translateY(-3px) scale(1.02)' : 'none')};
    box-shadow: ${({ $clickable, theme }) => ($clickable ? theme.shadows.lg : theme.shadows.sm)};
  }
  img { width: 100%; height: 100%; object-fit: contain; padding: 10px; box-sizing: border-box; animation: ${reveal} 0.45s ease-out; }
`;

const Spinner = styled.div`
  width: 22px;
  height: 22px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  text-align: center;
`;

const Hint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ErrText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error};
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export function AiLogoGenerator({ brandName, colors, industry, description, onLogoReady, onBack }: Props) {
  const [style, setStyle] = useState<string>('wordmark');
  const [phase, setPhase] = useState<'pick' | 'generating' | 'choose' | 'processing'>('pick');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [error, setError] = useState('');

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const poll = useCallback(async (taskIds: string[]) => {
    if (cancelledRef.current) return;
    try {
      const res = await apiClient.get<StatusResponse>(
        `/api/logo/status?taskIds=${encodeURIComponent(taskIds.join(','))}`,
      );
      if (cancelledRef.current) return;
      setVariants(res.tasks);
      const done = res.tasks.every((t) => t.state === 'success' || t.state === 'fail');
      if (done) {
        const anyOk = res.tasks.some((t) => t.state === 'success' && t.imageUrl);
        setPhase('choose');
        if (!anyOk) setError('No logos came out this time. Try another style or generate again.');
        return;
      }
      pollRef.current = setTimeout(() => poll(taskIds), 3000);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to check logo status');
      setPhase('pick');
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!brandName.trim()) {
      setError('Add your brand name first.');
      return;
    }
    setError('');
    setVariants([]);
    setPhase('generating');
    try {
      const res = await apiClient.post<GenerateResponse>('/api/logo/generate', {
        brandName,
        style,
        colors,
        industry,
        description,
      });
      if (cancelledRef.current) return;
      setVariants(res.taskIds.map((taskId) => ({ taskId, state: 'waiting' as const })));
      void poll(res.taskIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logo generation failed');
      setPhase('pick');
    }
  }, [brandName, style, colors, industry, description, poll]);

  const handlePick = useCallback(
    async (imageUrl: string) => {
      setPhase('processing');
      setError('');
      try {
        const blob = await removeWhiteBackground(imageUrl);
        const file = blobToLogoFile(blob, brandName);
        const previewUrl = URL.createObjectURL(blob);
        // Las otras variantes generadas (no elegidas) → se guardan como brand
        // assets extra (el padre las sube con isLogo=false).
        const extras = variants
          .filter((v) => v.state === 'success' && v.imageUrl && v.imageUrl !== imageUrl)
          .map((v) => v.imageUrl!);
        onLogoReady(file, previewUrl, extras);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not process the logo');
        setPhase('choose');
      }
    },
    [brandName, onLogoReady, variants],
  );

  // ─── Pick style ───
  if (phase === 'pick') {
    return (
      <Wrap>
        <StyleGrid>
          {LOGO_STYLE_OPTIONS.map((s) => (
            <StyleCard key={s.id} type="button" $selected={style === s.id} onClick={() => setStyle(s.id)}>
              <StyleIcon>{s.icon}</StyleIcon>
              <div>
                <StyleLabel>{s.label}</StyleLabel>
                <StyleDesc>{s.description}</StyleDesc>
              </div>
            </StyleCard>
          ))}
        </StyleGrid>
        {error && <ErrText>{error}</ErrText>}
        <Row>
          {onBack && (
            <Button type="button" $variant="outline" $size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="button" $size="sm" onClick={handleGenerate} style={{ flex: 1 }}>
            ✨ Generate logos
          </Button>
        </Row>
      </Wrap>
    );
  }

  // ─── Processing (removing background) ───
  if (phase === 'processing') {
    return (
      <Wrap>
        <Center>
          <Spinner />
          <Hint>Cleaning up your logo…</Hint>
        </Center>
      </Wrap>
    );
  }

  // ─── Generating / Choose (shared grid) ───
  const clickable = phase === 'choose';
  // Apenas arranca la generación (antes de que vuelvan los taskIds) mostramos 3
  // tiles con loader para no dejar un hueco vacío.
  const tiles: Variant[] = variants.length > 0
    ? variants
    : Array.from({ length: 3 }, (_, i) => ({ taskId: `ph-${i}`, state: 'waiting' as const }));
  return (
    <Wrap>
      <VariantGrid>
        {tiles.map((v) => (
          <VariantTile
            key={v.taskId}
            type="button"
            $clickable={clickable && v.state === 'success' && !!v.imageUrl}
            disabled={!(clickable && v.state === 'success' && v.imageUrl)}
            onClick={() => v.imageUrl && clickable && handlePick(v.imageUrl)}
            title={clickable && v.state === 'success' ? 'Use this logo' : undefined}
          >
            {v.state === 'success' && v.imageUrl ? (
              <img src={v.imageUrl} alt="Logo option" />
            ) : v.state === 'fail' ? (
              <Center style={{ padding: 6 }}>
                <Hint>✕</Hint>
              </Center>
            ) : (
              <>
                <LoaderVideo src="/loaders/ai-processing-square.mp4" autoPlay muted loop playsInline />
                <LoaderScrim />
                <LoaderLabel>Designing…</LoaderLabel>
              </>
            )}
          </VariantTile>
        ))}
      </VariantGrid>

      {phase === 'generating' && <Hint>Designing your logos… this takes ~40s.</Hint>}
      {phase === 'choose' && !error && <Hint>Tap a logo to use it. The white background is removed automatically.</Hint>}
      {error && <ErrText>{error}</ErrText>}

      {phase === 'choose' && (
        <Row>
          {onBack && (
            <Button type="button" $variant="outline" $size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="button" $variant="outline" $size="sm" onClick={() => setPhase('pick')}>
            Change style
          </Button>
          <Button type="button" $size="sm" onClick={handleGenerate} style={{ flex: 1 }}>
            Generate again
          </Button>
        </Row>
      )}
    </Wrap>
  );
}

export default AiLogoGenerator;
